// @flow

import React, { useRef, useEffect } from 'react'
import glamorous from 'glamorous'
import getContent from 'pp-react-l10n'

import { grey200, HeadingText } from '@paypalcorp/pp-react'

import Events from 'lib/analytics/event-tags'
import { trackImpression, tracking } from 'lib/analytics'

import { isPreDefinedSellerCoverPhoto } from 'utils/image-utils'

import { INSTRUMENTATION_MAP } from 'utils/constants'

const INSTRUMENTATION = INSTRUMENTATION_MAP['cover/select']

// import clientData from 'utils/client-data'
import Option from 'screens/shared/components/cover-option'

const l10n = getContent('pages/seller/photos')

const Wrapper = glamorous.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
})
const Container = glamorous.div({
  width: '34rem',

  '@media (max-width: 900px)': {
    width: '100%',
  },
})

const OptionsContainer = glamorous.div({
  display: 'flex',
  flexWrap: 'wrap',
  marginTop: '2.5rem',
})

const OptionContainer = glamorous.div({
  flex: '50%',
  margin: '0.65rem 0',
  boxSizing: 'border-box',
  '&:nth-child(odd)': {
    paddingRight: '0.25rem',
  },
  '&:nth-child(even)': {
    paddingLeft: '0.25rem',
  },
  display: 'flex',
  justifyContent: 'flex-start',

  '@media (max-width: 640px)': {
    flex: '100%',
  },
  '@media(max-width: 900px)': {
    justifyContent: 'center',
  },
})

const EditCurrentContainer = glamorous.div({
  borderTop: `0.15rem dotted ${grey200}`,
  paddingTop: '1.5rem',
  marginTop: '1.5rem',
})

type Props = {
  history: {
    push: () => any,
  },
  sellerProfileState: SellerProfileState,
}

function CoverSelect(props: Props) {
  const {
    sellerProfileState: {
      state: {
        photoSettings: {
          initialCoverPhoto,
          cachedCoverPhoto = initialCoverPhoto,
          coverPhotoVerticalPan = '50',
          suggestedCoverPhotos = [],
        } = {},
      } = {},
    } = {},
  } = props

  useEffect(() => {
    trackImpression(
      INSTRUMENTATION.OLD.PAGE_GROUP,
      INSTRUMENTATION.OLD.PAGE_NAME,
    )
    tracking(Events.public_identity_background_edit_screen_shown({}))
  }, [])

  let STOCK_PHOTOS_URLS = suggestedCoverPhotos.map((photo) => {
    // mapping an a11y string for a particular cover photo
    const url = new URL(photo.reference_url)
    const path = url.pathname.split('/')
    const fileName = path[path.length - 1].split('.')[0]
    const a11yPropertyKey = `a11y.label.cover.${fileName}`

    return {
      photoUrl: photo && photo.reference_url,
      photoLabel: l10n(`${a11yPropertyKey}`), // l10n('a11y.label.cover.pink'),
      testId: `${fileName}-cover-photo`,
    }
  })

  const fileInput = useRef(null)

  const backToInitial = () => {
    props.history.push('/seller/photos')
  }

  const advanceToPan = () => {
    props.sellerProfileState.setDraggableCoverPhoto(true)
    props.history.push('/seller/photos/cover/pan')
  }

  const onUploadSelect = () => {
    // TODO: Add instrumentation
    fileInput.current.click()
  }

  const onUploadPhoto = (e: SyntheticInputEvent<HTMLInputElement>) => {
    props.sellerProfileState.pan('50', '0')
    props.sellerProfileState.loadImage(e, advanceToPan)
  }

  const onPaypalStockPhotoSelect = (photoUrl) => {
    props.sellerProfileState.pan('0', '0')
    props.sellerProfileState.userImagePreviewUrl(photoUrl, '', false)

    backToInitial()
  }

  const onEditCurrentSelect = (e: SyntheticInputEvent<HTMLAnchorElement>) => {
    e.preventDefault()

    props.sellerProfileState.pan(coverPhotoVerticalPan, '0')
    props.sellerProfileState.userImagePreviewUrl(cachedCoverPhoto, '', false)

    advanceToPan()
  }

  return (
    <Wrapper>
      <Container>
        <HeadingText size="sm">{l10n('cover.select.header')}</HeadingText>
        <OptionsContainer>
          <OptionContainer>
            <Option onSelect={onUploadSelect} />
            <input
              ref={fileInput}
              style={{ display: 'none' }}
              type="file"
              accept="image/*"
              onChange={onUploadPhoto}
              data-test-id="upload-cover-photo"
            />
          </OptionContainer>
          {STOCK_PHOTOS_URLS.map(({ photoUrl, photoLabel, testId }) => (
            <OptionContainer key={photoUrl} data-test-id={testId}>
              <Option
                photoUrl={photoUrl}
                photoLabel={photoLabel}
                onSelect={onPaypalStockPhotoSelect}
              />
            </OptionContainer>
          ))}
        </OptionsContainer>
        {!isPreDefinedSellerCoverPhoto(cachedCoverPhoto) && (
          <EditCurrentContainer>
            <button
              className="vx_btn-link"
              onClick={onEditCurrentSelect}
              data-test-id="edit-current-cover-photo"
            >
              {l10n('cover.select.link.editCurrent')}
            </button>
          </EditCurrentContainer>
        )}
      </Container>
    </Wrapper>
  )
}

export default CoverSelect
